import { Col, Row } from 'antd';
import type { TableProps } from 'antd';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AppTable from '../../../../components/AppTable/AppTable';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../redux/store';
import { formatNumber } from '../../../../utils/helpers/format.helper';
import dayjs from 'dayjs';
import { fetchClientBasedStatisticsRequest, fetchAllCompanyNamesRequest } from '../../../../redux/reducers/dashboard/dashboardSlice';
import { ClientBasedStatisticsData } from '../../../../redux/reducers/dashboard/dashboard.types';

const ClientBasedWaterUsageTable = () => {
    const { t } = useTranslation('dashboard');
    const dispatch = useDispatch();
    const { clientBaseStatistics, allCompanyNames } = useSelector((state: RootState) => state.dashboard);
    const { data, loading, total, page, limit } = clientBaseStatistics;
    const companyFilterOptions = allCompanyNames.data.map(name => ({ text: name, value: name }));

    const [filteredInfo, setFilteredInfo] = useState<Record<string, string[] | null>>({});

    const columns: TableProps<ClientBasedStatisticsData>['columns'] = [
        {
            title: <span className="base-table-text">{t('company_name')}</span>,
            dataIndex: 'companyName',
            key: 'companyName',
            filters: companyFilterOptions,
            filteredValue: filteredInfo.companyName || null,
            filterSearch: true,
            render: (text: string) => <span className="base-table-text">{text}</span>
        },
        {
            title: <span className="base-table-text">{t('total_input_water')}</span>,
            dataIndex: 'totalConsumption',
            key: 'totalConsumption',
            render: (text: number) => <span className="base-table-text">{formatNumber(text || 0)}</span>
        },
        {
            title: <span className="base-table-text">{t('start_date')}</span>,
            dataIndex: 'firstRecordDate',
            key: 'firstRecordDate',
            render: (text: string) => <span className="base-table-text">{text ? dayjs(text).format("YYYY-MM-DD") : "-"}</span>
        },
        {
            title: <span className="base-table-text">{t('end_date')}</span>,
            dataIndex: 'lastRecordDate',
            key: 'lastRecordDate',
            render: (text: string) => <span className="base-table-text">{text ? dayjs(text).format("YYYY-MM-DD") : "-"}</span>
        },
        {
            title: <span className="base-table-text">{t('meter_data_received_count')}</span>,
            dataIndex: 'distinctDeviceCount',
            key: 'distinctDeviceCount',
            render: (text: string) => <span className="base-table-text">{text ? text : "0"}</span>
        },
        {
            title: <span className="base-table-text">{t('anomaly_count')}</span>,
            dataIndex: 'notificationCount',
            key: 'notificationCount',
            render: (text: string) => <span className="base-table-text">{text ? text : "0"}</span>
        }
    ];

    const handleTableChange: TableProps<ClientBasedStatisticsData>['onChange'] = (pagination, filters) => {
        setFilteredInfo(filters as Record<string, string[] | null>);

        dispatch(fetchClientBasedStatisticsRequest({
            page: pagination.current || 1,
            limit: pagination.pageSize || 5,
            companyName: (filters.companyName as string[]) || [],
        }));
    };

    useEffect(() => {
        dispatch(fetchAllCompanyNamesRequest());
    }, [dispatch]);

    return (
        <Row>
            <Col xs={24}>
                <AppTable
                    className='base-table-body'
                    loading={loading}
                    dataSource={data}
                    columns={columns}
                    onChange={handleTableChange}
                    rowKey="companyId"
                    paginationConfig={{
                        pageSize: limit,
                        current: page,
                        total: total,
                        showTotal: (total: number, range: [number, number]) =>
                            t('records_pagination', {
                                start: range[0],
                                end: range[1],
                                total: total
                            })
                    }}
                />
            </Col>
        </Row>
    );
}

export default ClientBasedWaterUsageTable;